import { isEighteenOrOlder, isValid } from '@eneco/common/date';
import * as yup from 'yup';

const NAME_CHARACTERS_PATTERN = /^(?=.*[\w\s])[^\w]*.*$/;

type DateFieldMessages = {
  requiredMessage?: string;
  validationMessage?: string;
};

type BirthdateFieldMessages = {
  validationMessage?: string;
  validationMinValueMessage?: string;
};

interface PersonalDetailsNameSchemaOptions {
  firstNameRequired?: string;
  firstNameValidation?: string;
  surnameRequired?: string;
  surnameValidation?: string;
  validateNameCharacters?: boolean;
  initials?: {
    required?: string;
    maxMessage: string;
  };
}

interface PersonalDetailsBirthdateSchemaOptions {
  day?: DateFieldMessages;
  month?: DateFieldMessages;
  year?: DateFieldMessages;
  birthdate?: BirthdateFieldMessages;
  birthdateKind: 'object' | 'string';
}

const nameField = (required?: string, validation?: string, validateNameCharacters?: boolean) => {
  const schema = yup.string().required(required);

  return validateNameCharacters ? schema.matches(NAME_CHARACTERS_PATTERN, validation) : schema;
};

const birthdateTests = (schema: yup.AnySchema, birthdate?: BirthdateFieldMessages) =>
  schema
    .test('valid date', birthdate?.validationMessage ?? '', (_, context) => {
      const {
        parent: { day, month, year },
      } = context;

      if (!day || !month || !year) {
        return false;
      }

      return isValid(year, month, day);
    })
    .test('minimum age', birthdate?.validationMinValueMessage ?? '', (_, context) => {
      const {
        parent: { day, month, year },
      } = context;

      return isEighteenOrOlder(year, month, day);
    });

export const personalDetailsNameFields = ({
  firstNameRequired,
  firstNameValidation,
  surnameRequired,
  surnameValidation,
  validateNameCharacters = false,
  initials,
}: PersonalDetailsNameSchemaOptions) => ({
  ...(initials
    ? {
        initials: yup
          .string()
          .required(initials.required)
          .test('initials max', initials.maxMessage, (value = '') => {
            return value.toUpperCase().replace(/[^A-Z]/g, '').length <= 6;
          }),
      }
    : {}),
  firstName: nameField(firstNameRequired, firstNameValidation, validateNameCharacters),
  surnamePreposition: yup.string(),
  surname: nameField(surnameRequired, surnameValidation, validateNameCharacters),
});

export const personalDetailsBirthdateSchema = ({
  day,
  month,
  year,
  birthdate,
  birthdateKind,
}: PersonalDetailsBirthdateSchemaOptions) => ({
  day: yup
    .number()
    .typeError(day?.validationMessage ?? '')
    .required(day?.requiredMessage)
    .min(1, day?.validationMessage)
    .max(31, day?.validationMessage),
  month: yup
    .number()
    .typeError(month?.validationMessage ?? '')
    .required(month?.requiredMessage)
    .min(1, month?.validationMessage)
    .max(12, month?.validationMessage),
  year: yup
    .number()
    .typeError(year?.validationMessage ?? '')
    .required(year?.requiredMessage)
    .min(1900, year?.validationMessage),
  birthdate: birthdateTests(birthdateKind === 'object' ? yup.object({}) : yup.string(), birthdate),
});

export const formatPersonalDetailsBirthdate = (
  formatShort: (value: Date) => string,
  formData: { day: string | number; month: string | number; year: string | number },
) => formatShort(new Date(Number(formData.year), Number(formData.month) - 1, Number(formData.day)));
